/*!
 * timecode.js — SMPTE timecode as it arrives over MIDI (MTC)
 * © 2026 Rob Canning — GPLv3
 *
 * A DAW or video player that plays sends MIDI Time Code: eight quarter-frame
 * messages (F1 0n) spread over every two frames, each carrying a nibble of
 * hours, minutes, seconds, frames and the frame rate; when it locates while
 * stopped it sends the whole position at once (a full-frame SysEx:
 * F0 7F 7F 01 01 hh mm ss ff F7). The decoder turns either into seconds.
 *
 * A set of eight quarter frames describes the moment its first one was
 * sent; by the time the eighth arrives two frames have passed, so the
 * position reported is the assembled one plus two frames. Pieces arriving
 * in falling order mean the source is running backwards (scrubbing).
 *
 * Frame rates (the rate bits of the hours byte): 24, 25, 29.97 drop-frame,
 * 30. Drop-frame counts as SMPTE does: frames 0 and 1 are skipped at the
 * start of every minute except each tenth.
 *
 * Pure: bytes and times in, positions out -- testable without MIDI.
 */

export const FPS_CODES = [24, 25, 29.97, 30];

/** Seconds from h:m:s:f at a rate (29.97 = drop-frame). */
export function tcToSeconds(h, m, s, f, fps = 25) {
  if (fps === 29.97) {
    const mins = h * 60 + m;
    const frames = mins * 60 * 30 + s * 30 + f - 2 * (mins - Math.floor(mins / 10));
    return frames / (30000 / 1001);
  }
  return h * 3600 + m * 60 + s + f / fps;
}

/** "01:00:00:00", "01:00:00;00" (drop-frame), "1:00:00" or a number of seconds -> seconds. */
export function parseTimecode(v, fps = 25) {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v).trim();
  if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s);
  const p = s.split(/[:;.]/).map(Number);
  if (p.some(n => !Number.isFinite(n))) return 0;
  const [h = 0, m = 0, sec = 0, f = 0] = p.length === 3 ? [p[0], p[1], p[2], 0] : p;
  return tcToSeconds(h, m, sec, f, fps);
}

/** Seconds -> "hh:mm:ss:ff" at a rate (for showing where the source is). */
export function formatTimecode(sec, fps = 25) {
  const r = fps === 29.97 ? 30 : fps;
  const neg = sec < 0;
  let t = Math.abs(sec);
  const h = Math.floor(t / 3600); t -= h * 3600;
  const m = Math.floor(t / 60); t -= m * 60;
  const s = Math.floor(t);
  const f = Math.min(r - 1, Math.floor((t - s) * r + 1e-6));
  const two = n => String(n).padStart(2, "0");
  return `${neg ? "-" : ""}${two(h)}:${two(m)}:${two(s)}${fps === 29.97 ? ";" : ":"}${two(f)}`;
}

/**
 * Feed it MIDI messages; it says where the source is.
 *   feed(bytes, at) -> null | { sec, fps, at, running, reverse, full }
 * running: from quarter frames (the source plays); full: a full-frame
 * message (a locate, usually while stopped).
 */
export function createMtcDecoder() {
  const nib = new Array(8).fill(0);
  let lastPiece = -1, seen = 0, reverse = false;

  return {
    feed(bytes, at) {
      if (!bytes || !bytes.length) return null;
      // quarter frame: F1 0n -- piece n (0-7), 4 bits of data
      if (bytes[0] === 0xf1 && bytes.length >= 2) {
        const piece = (bytes[1] >> 4) & 0x07, val = bytes[1] & 0x0f;
        if (lastPiece >= 0) {
          if (piece === (lastPiece + 1) % 8) reverse = false;
          else if (piece === (lastPiece + 7) % 8) reverse = true;
          else seen = 0;                      // a gap: start the set again
        }
        lastPiece = piece;
        nib[piece] = val;
        seen = Math.min(8, seen + 1);
        // a set is complete at piece 7 going forward, at piece 0 backwards
        if (seen < 8 || piece !== (reverse ? 0 : 7)) return null;
        const f = nib[0] | (nib[1] << 4);
        const s = nib[2] | (nib[3] << 4);
        const m = nib[4] | (nib[5] << 4);
        const h = nib[6] | ((nib[7] & 0x01) << 4);
        const fps = FPS_CODES[(nib[7] >> 1) & 0x03];
        const frame = 1 / (fps === 29.97 ? 30000 / 1001 : fps);
        const sec = tcToSeconds(h, m, s, f, fps) + (reverse ? -2 : 2) * frame;
        return { sec, fps, at, running: true, reverse, full: false };
      }
      // full frame: F0 7F <dev> 01 01 hh mm ss ff F7
      if (bytes[0] === 0xf0 && bytes.length >= 10 && bytes[1] === 0x7f && bytes[3] === 0x01 && bytes[4] === 0x01) {
        const hb = bytes[5];
        const fps = FPS_CODES[(hb >> 5) & 0x03];
        const sec = tcToSeconds(hb & 0x1f, bytes[6], bytes[7], bytes[8], fps);
        seen = 0; lastPiece = -1;
        return { sec, fps, at, running: false, reverse: false, full: true };
      }
      return null;
    },
    reset() { seen = 0; lastPiece = -1; reverse = false; },
  };
}

/** The quarter-frame messages a source at `sec` sends (for tests and a simulator). */
export function quarterFrames(sec, fps = 25) {
  const code = FPS_CODES.indexOf(fps);
  const r = fps === 29.97 ? 30000 / 1001 : fps;
  let total = Math.floor(sec * r + 1e-6);
  if (fps === 29.97) {
    // back to drop-frame labels
    const d = Math.floor(total / 17982), mod = total % 17982;
    total += 18 * d + (mod > 1 ? 2 * Math.floor((mod - 2) / 1798) : 0);
  }
  const rr = fps === 29.97 ? 30 : fps;
  const f = total % rr, s = Math.floor(total / rr) % 60, m = Math.floor(total / rr / 60) % 60, h = Math.floor(total / rr / 3600) % 24;
  const vals = [f & 15, f >> 4, s & 15, s >> 4, m & 15, m >> 4, h & 15, ((h >> 4) & 1) | (code << 1)];
  return vals.map((v, n) => [0xf1, (n << 4) | v]);
}

/*
 * MIDI clock and Song Position Pointer -- what Ableton Live, Bitwig and
 * hardware sequencers send instead of MTC. Musical position, not time:
 *
 *   F8          a clock tick, 24 to a beat (quarter note)
 *   FA          start: from the top (beat 0), on the next tick
 *   FB          continue: from where the position says
 *   FC          stop
 *   F2 ll mm    song position, in 16ths (6 ticks), usually while stopped
 *
 * The decoder keeps the beat position and the tempo, measured from the
 * ticks (averaged: a USB tick jitters by a millisecond or two).
 *   feed(bytes, at) -> null | { beats, bpm, running, at, kind }
 * kind: "tick" (running), "position" (a locate), "start", "continue", "stop".
 */
export function createClockDecoder({ average = 24 } = {}) {
  let beats = 0, running = false, bpm = null;
  let lastTick = null, armed = false;          // armed: started, waiting for the first tick
  const gaps = [];
  const ev = (kind, at) => ({ kind, beats, bpm, running, at });
  return {
    feed(bytes, at) {
      const b = bytes?.[0];
      if (b === 0xf2 && bytes.length >= 3) {
        beats = ((bytes[1] & 0x7f) | ((bytes[2] & 0x7f) << 7)) / 4;
        lastTick = null;
        return ev("position", at);
      }
      if (b === 0xfa) { beats = 0; running = true; armed = true; lastTick = null; return ev("start", at); }
      if (b === 0xfb) { running = true; armed = true; lastTick = null; return ev("continue", at); }
      if (b === 0xfc) { running = false; armed = false; lastTick = null; return ev("stop", at); }
      if (b !== 0xf8) return null;
      // tempo from the spacing of ticks, whether or not the transport runs
      // (many sources send clock while stopped)
      if (lastTick != null) {
        const gap = at - lastTick;
        if (gap > 0 && gap < 250) {                 // under 10 bpm is not a clock
          gaps.push(gap);
          if (gaps.length > average) gaps.shift();
          const mean = gaps.reduce((s, g) => s + g, 0) / gaps.length;
          bpm = 60000 / (mean * 24);
        }
      }
      lastTick = at;
      if (!running) return null;
      // the first tick after start/continue is the position itself; each one after it a 24th of a beat on
      if (armed) armed = false;
      else beats += 1 / 24;
      return ev("tick", at);
    },
    reset() { beats = 0; running = false; bpm = null; lastTick = null; armed = false; gaps.length = 0; },
  };
}

/** The clock bytes a source at `bpm` sends for `beats` beats (for tests and a simulator). */
export function clockTicks(beats) {
  return Array.from({ length: Math.round(beats * 24) }, () => [0xf8]);
}

/** Song Position Pointer for a position in beats (quarter notes). */
export function songPosition(beats) {
  const sixteenths = Math.round(beats * 4);
  return [0xf2, sixteenths & 0x7f, (sixteenths >> 7) & 0x7f];
}
